import { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import { useAppTheme } from "@/hooks/useAppTheme";
import { ResourceName, type TemplateUsageStats } from "@/types";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import { formatSuccessRate } from "@/resources/admin/task-templates/taskConfigUtils";

interface UsageStatsSectionProps {
  templateId: string;
}

interface StatCardProps {
  label: string;
  value: string | number;
  color?: string;
}

const StatCard = ({ label, value, color }: StatCardProps) => {
  const { isDarkMode } = useAppTheme();

  return (
    <Box
      sx={{
        flex: 1,
        minWidth: 90,
        p: 1.5,
        borderRadius: 1,
        bgcolor: isDarkMode ? "grey.900" : "grey.50",
        border: 1,
        borderColor: "divider",
        textAlign: "center",
      }}
    >
      <Typography variant="h4" sx={{ color: color || "text.primary", fontWeight: 700 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  );
};

export const UsageStatsSection = ({ templateId }: UsageStatsSectionProps) => {
  const [stats, setStats] = useState<TemplateUsageStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchStats = async () => {
      setIsLoading(true);
      setError(false);
      const apiUrl = getPartnerAwareApiUrl();

      try {
        const { json } = await httpClient(`${apiUrl}/${ResourceName.TASK_TEMPLATES}/${templateId}/usage-stats`);
        setStats(json as TemplateUsageStats);
      } catch {
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    void fetchStats();
  }, [templateId]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !stats) {
    return (
      <Typography variant="body2" color="text.secondary">
        No usage data available.
      </Typography>
    );
  }

  return (
    <Box display="flex" gap={1} flexWrap="wrap">
      <StatCard label="Created" value={stats.tasks_created} />
      <StatCard label="Completed" value={stats.tasks_completed} color="success.main" />
      <StatCard label="Open" value={stats.tasks_open} color="info.main" />
      <StatCard label="In Progress" value={stats.tasks_in_progress} color="warning.main" />
      <StatCard label="Success Rate" value={formatSuccessRate(stats.success_rate)} color="primary.main" />
    </Box>
  );
};
